import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cn } from '../../../lib/utils';

export type CharlieAvatarProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Root
>;

export type CharlieAvatarImageProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Image
>;

export type CharlieAvatarFallbackProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Fallback
>;

/**
 * CharlieAvatar — React equivalent of Rocket's `RocketAvatar`.
 *
 * An image with a fallback (initials, a monogram or an icon) that shows while
 * the image loads or when it fails. Built on
 * [Radix Avatar](https://www.radix-ui.com/primitives/docs/components/avatar)
 * and styled like the [ShadCN Avatar](https://ui.shadcn.com/docs/components/avatar).
 *
 * ## Usage
 *
 * ```tsx
 * import {
 *   CharlieAvatar,
 *   CharlieAvatarImage,
 *   CharlieAvatarFallback,
 * } from '@ver.id/libs.ui.charlie';
 *
 * // Round avatar, 32px by default
 * <CharlieAvatar>
 *   <CharlieAvatarImage src={user.photo} alt={user.name} />
 *   <CharlieAvatarFallback>KS</CharlieAvatarFallback>
 * </CharlieAvatar>
 *
 * // Square app logo with a monogram fallback
 * <CharlieAvatar className="size-10 rounded-lg">
 *   <CharlieAvatarImage src={app.logo} alt={app.name} className="object-contain" />
 *   <CharlieAvatarFallback className="rounded-lg text-xs font-semibold">
 *     EU
 *   </CharlieAvatarFallback>
 * </CharlieAvatar>
 * ```
 *
 * ## Gotchas
 *
 * - **The image only mounts once it has loaded.** Until then, and when it
 *   fails, the fallback renders instead. Pass `delayMs` to the fallback to
 *   avoid a flash for images that load quickly.
 * - **Shape lives on both parts.** Set the radius on `CharlieAvatar` (it clips
 *   the image) and on `CharlieAvatarFallback` (it has its own background).
 * - **Logos**: add `object-contain` to the image so marks are not cropped.
 */
export function CharlieAvatar({ className, ...props }: CharlieAvatarProps) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        'relative flex size-8 shrink-0 overflow-hidden rounded-full',
        className,
      )}
      {...props}
    />
  );
}

/** The avatar's image. Renders only after it has loaded. */
export function CharlieAvatarImage({
  className,
  ...props
}: CharlieAvatarImageProps) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full', className)}
      {...props}
    />
  );
}

/** Shown while the image loads, when it fails, or when there is no `src`. */
export function CharlieAvatarFallback({
  className,
  ...props
}: CharlieAvatarFallbackProps) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'bg-muted flex size-full items-center justify-center rounded-full',
        className,
      )}
      {...props}
    />
  );
}
